import { useEffect, useState } from 'react';
import api from '../../api/axios';
import S3ImagePicker from '../components/S3ImagePicker';

const EMPTY_BANNER = { image: '', title: '', subTitle: '', alt: '' };

export default function HeroManager() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_BANNER);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    api.get('/content/hero')
      .then(r => setBanners(r.data?.banners || []))
      .finally(() => setLoading(false));
  }, []);

  const persist = async (updated) => {
    setSaving(true); setSaved(false); setError('');
    try {
      await api.put('/content/hero', { banners: updated });
      setBanners(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const openAdd = () => { setEditIdx('new'); setEditForm(EMPTY_BANNER); };
  const openEdit = (i) => { setEditIdx(i); setEditForm({ ...banners[i] }); };
  const closeEdit = () => { setEditIdx(null); setEditForm(EMPTY_BANNER); };

  const saveEdit = async () => {
    if (!editForm.image) { setError('Please select a banner image.'); return; }
    const updated = editIdx === 'new'
      ? [...banners, editForm]
      : banners.map((b, i) => i === editIdx ? editForm : b);
    closeEdit();
    await persist(updated);
  };

  const deleteBanner = async (i) => {
    if (!window.confirm('Remove this banner slide?')) return;
    await persist(banners.filter((_, idx) => idx !== i));
  };

  const move = async (i, dir) => {
    const arr = [...banners];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    await persist(arr);
  };

  const ef = (k) => ({ value: editForm[k], onChange: e => setEditForm(f => ({ ...f, [k]: e.target.value })) });

  if (loading) return <div className="text-center py-5"><span className="spinner-border spinner-border-sm"></span></div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-0">Hero Banners</h4>
          <p className="text-muted small mb-0">Manage the homepage hero slideshow images and text</p>
        </div>
        <button className="btn btn-primary rounded-pill px-4" onClick={openAdd}>
          <i className="fas fa-plus me-2"></i>Add Slide
        </button>
      </div>

      {saved && <div className="alert alert-success py-2 mb-3"><i className="fas fa-check-circle me-2"></i>Saved successfully.</div>}
      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

      {banners.length === 0 ? (
        <div className="bg-white rounded shadow-sm p-5 text-center text-muted">
          <i className="fas fa-images fa-3x mb-3 opacity-25 d-block"></i>
          No banners yet. Click "Add Slide" to get started.
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {banners.map((b, i) => (
            <div key={i} className="bg-white rounded shadow-sm d-flex align-items-center gap-3 p-3">
              {/* Thumbnail */}
              <div style={{ width: 140, height: 80, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: '#e9ecef' }}>
                {b.image ? (
                  <img src={b.image.startsWith('http') ? b.image : `${window.location.origin}${b.image}`}
                    alt={b.alt || 'Banner'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                    <i className="fas fa-image fa-2x opacity-25"></i>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-grow-1 min-width-0">
                <div className="fw-semibold text-truncate">{b.title || <span className="text-muted">(No title)</span>}</div>
                <div className="text-muted small text-truncate">{b.subTitle || '—'}</div>
                <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: 2 }}>{b.image || '—'}</div>
              </div>

              {/* Actions */}
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div className="d-flex flex-column gap-1">
                  <button className="btn btn-sm btn-outline-secondary py-0 px-2" disabled={i === 0} onClick={() => move(i, -1)} title="Move up"><i className="fas fa-chevron-up" style={{ fontSize: '0.7rem' }}></i></button>
                  <button className="btn btn-sm btn-outline-secondary py-0 px-2" disabled={i === banners.length - 1} onClick={() => move(i, 1)} title="Move down"><i className="fas fa-chevron-down" style={{ fontSize: '0.7rem' }}></i></button>
                </div>
                <span className="badge bg-light text-secondary" style={{ minWidth: 28 }}>{i + 1}</span>
                <button className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={() => openEdit(i)}><i className="fas fa-pencil-alt me-1"></i>Edit</button>
                <button className="btn btn-sm btn-outline-danger rounded-pill px-3" onClick={() => deleteBanner(i)}><i className="fas fa-trash me-1"></i>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Add modal */}
      {editIdx !== null && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={closeEdit}>
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editIdx === 'new' ? 'Add Slide' : `Edit Slide ${editIdx + 1}`}</h5>
                <button className="btn-close" onClick={closeEdit}></button>
              </div>
              <div className="modal-body">
                {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

                {/* Image preview + picker */}
                <div className="mb-3">
                  <label className="form-label small fw-bold">Banner Image *</label>
                  <div className="d-flex gap-3 align-items-start">
                    <div style={{ width: 200, height: 110, borderRadius: 8, overflow: 'hidden', background: '#e9ecef', flexShrink: 0 }}>
                      {editForm.image ? (
                        <img src={editForm.image.startsWith('http') ? editForm.image : `${window.location.origin}${editForm.image}`}
                          alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                          <i className="fas fa-image fa-2x opacity-25"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <input className="form-control mb-2" placeholder="Image URL or pick from media" value={editForm.image} onChange={e => setEditForm(f => ({ ...f, image: e.target.value }))} />
                      <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setShowPicker(true)}>
                        <i className="fas fa-folder-open me-1"></i>Browse Media
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-bold">Title</label>
                  <input className="form-control" placeholder="Main heading text" {...ef('title')} />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Subtitle</label>
                  <input className="form-control" placeholder="Supporting text below the title" {...ef('subTitle')} />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Alt Text</label>
                  <input className="form-control" placeholder="Describe the image for accessibility" {...ef('alt')} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary rounded-pill px-5" onClick={saveEdit} disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : <><i className="fas fa-save me-2"></i>Save Slide</>}
                </button>
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={closeEdit}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPicker && (
        <S3ImagePicker
          onSelect={url => { setEditForm(f => ({ ...f, image: url })); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
